import { create } from 'zustand';
import type { DiceResult } from '../components/DiceRoller';
import { supabase, DbChatMessage } from '../lib/supabase';
import { createSubscription } from '../lib/realtimeHelpers';
import { notify } from './notificationStore';
import type { Open5eSpell } from '@machina/core';

// Common D&D roll types with keywords and display names
export const ROLL_LABELS = {
  // Initiative
  init: { label: 'initiative', display: 'Initiative' },
  initiative: { label: 'initiative', display: 'Initiative' },
  // Attack
  attack: { label: 'attack', display: 'Attack Roll' },
  atk: { label: 'attack', display: 'Attack Roll' },
  hit: { label: 'attack', display: 'Attack Roll' },
  // Critical hit
  crit: { label: 'crit', display: 'CRITICAL HIT!' },
  critical: { label: 'crit', display: 'CRITICAL HIT!' },
  // Damage
  damage: { label: 'damage', display: 'Damage' },
  dmg: { label: 'damage', display: 'Damage' },
  // Critical damage
  'crit-damage': { label: 'crit-damage', display: 'CRIT DAMAGE!' },
  critdamage: { label: 'crit-damage', display: 'CRIT DAMAGE!' },
  // Saves
  save: { label: 'save', display: 'Saving Throw' },
  saving: { label: 'save', display: 'Saving Throw' },
  // Ability checks
  check: { label: 'check', display: 'Ability Check' },
  skill: { label: 'check', display: 'Skill Check' },
  // Healing
  heal: { label: 'heal', display: 'Healing' },
  healing: { label: 'heal', display: 'Healing' },
  // Death saves
  death: { label: 'death', display: 'Death Save' },
  deathsave: { label: 'death', display: 'Death Save' },
} as const;

export type RollLabel = 'initiative' | 'attack' | 'damage' | 'save' | 'check' | 'heal' | 'death' | 'crit' | 'crit-damage' | null;

// Helper to parse roll label from keyword
export function parseRollLabel(keyword: string): { label: RollLabel; display: string } | null {
  const entry = ROLL_LABELS[keyword.toLowerCase() as keyof typeof ROLL_LABELS];
  return entry ? { label: entry.label as RollLabel, display: entry.display } : null;
}

export interface SavePrompt {
  dc: number;
  ability: string;
  spellName: string;
  casterName: string;
}

export interface RollRequestData {
  id: string;
  rollType: 'ability_check' | 'saving_throw' | 'skill_check' | 'attack' | 'custom';
  ability?: string;
  skill?: string;
  dc: number;
  reason: string;
  hideDc: boolean;
  resolved: boolean;
}

export interface ChatMessage {
  id: string;
  sender: string;
  message: string;
  timestamp: Date;
  type: 'player' | 'dm' | 'system' | 'dice' | 'spell' | 'npc' | 'ai_dm' | 'roll_request' | 'ai_dm_choices';
  diceResult?: DiceResult;
  // For dice rolls, track the character and roll context
  characterName?: string;
  rollType?: 'normal' | 'advantage' | 'disadvantage';
  rollLabel?: RollLabel; // e.g. "initiative", "attack", "save"
  // For spell lookups
  spellData?: Open5eSpell;
  // For spell damage (shows Roll Damage button on spell card)
  spellDamage?: { dice: string; damageType: string; spellName: string };
  // For healing spells (shows Roll Healing button on spell card)
  spellHealing?: { dice: string; spellName: string; abilityMod: number };
  // For weapon attacks (shows Roll Damage button beneath the attack roll).
  // Mirrors spellDamage. `dice`/`modifier` are the BASE values; the button
  // applies the 5e crit rule (double dice, add modifier once) when isCrit.
  weaponDamage?: {
    weaponName: string;
    dice: string;       // base dice, e.g. "1d8" (no modifier)
    dieCount: number;
    dieSize: number;
    modifier: number;   // flat damage bonus, added ONCE even on crit
    damageType: string;
    isCrit: boolean;
  };
  // For save-based spells (GM gets inline save-roll buttons)
  savePrompt?: SavePrompt;
  // For iso sprite generation prompt (GM-only)
  isoSpritePrompt?: boolean;
  // For mob intro / showcase card (cinematic reveal, not full stats)
  mobIntro?: {
    name: string;
    imageUrl?: string;
    tagline?: string;        // short dramatic line (≤120 chars)
    description?: string;    // 1-3 sentences of lore / visible traits (no stats)
    size?: string;           // "huge", "medium" — flavor text
    creatureType?: string;   // "undead", "yuan-ti" — flavor text
    hint?: string;           // GM-authored hint for players ("something is wrong here")
  };
  // For NPC suggestion mode — GM approves before the action fires
  npcSuggestion?: {
    npcId: string;
    npcName: string;
    summary: string;            // plain-English description of proposed action
    decisionJson: string;       // serialized NpcDecision for re-execution on accept
    status: 'pending' | 'executed' | 'skipped';
  };
  // For NPC dialogue (type: 'npc')
  npcId?: string;
  npcName?: string;
  npcTitle?: string;
  factionColor?: string;
  // For AI DM roll requests (type: 'roll_request')
  rollRequestData?: RollRequestData;
  // For AI DM choice buttons (type: 'ai_dm_choices')
  choiceButtons?: Array<{ label: string; action: string }>;
}

interface ChatState {
  messages: ChatMessage[];
  realtimeCleanup: (() => void) | null;
  currentCampaignId: string | null;
  currentUserId: string | null;
  isLoading: boolean;

  // Actions
  setCurrentUser: (userId: string | null) => void;
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void;
  // Streaming (solo AI DM): create a local-only message, append text as
  // chunks arrive, persist ONCE on finalize. Returns the message id.
  beginStreamingMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => string;
  appendStreamingText: (id: string, text: string) => void;
  finalizeStreamingMessage: (
    id: string,
    final?: { message?: string; choiceButtons?: ChatMessage['choiceButtons'] },
  ) => void;
  addDiceRoll: (params: {
    sender: string;
    characterName?: string;
    notation: string;
    result: DiceResult;
    rollType?: 'normal' | 'advantage' | 'disadvantage';
    rollLabel?: RollLabel;
    weaponDamage?: ChatMessage['weaponDamage'];
  }) => void;
  addSpellLookup: (params: {
    sender: string;
    characterName?: string;
    spell: Open5eSpell;
    spellDamage?: { dice: string; damageType: string; spellName: string };
    spellHealing?: { dice: string; spellName: string; abilityMod: number };
  }) => void;
  addSavePrompt: (params: { casterName: string; spellName: string; dc: number; ability: string }) => void;
  addMobIntro: (params: NonNullable<ChatMessage['mobIntro']>) => void;
  addNpcMessage: (params: {
    npcId: string;
    npcName: string;
    npcTitle?: string;
    factionColor?: string;
    message: string;
  }) => void;
  addSystemMessage: (message: string) => void;
  clearMessages: () => void;

  // Supabase sync
  loadFromSupabase: (campaignId: string, limit?: number) => Promise<void>;
  saveMessageToSupabase: (message: ChatMessage, campaignId: string, userId?: string) => Promise<void>;
  subscribeToRealtime: (campaignId: string) => void;
  unsubscribeFromRealtime: () => void;

  // Campaign isolation — wipes messages before a campaign switch so the
  // previous campaign's chat history isn't visible during the load gap.
  clearLocalState: () => void;
}

// Convert ChatMessage to DB format
function messageToDb(msg: ChatMessage, campaignId: string, userId?: string): Omit<DbChatMessage, 'id' | 'created_at'> {
  // Map client-only types to DB-safe values
  let dbType: DbChatMessage['message_type'] =
    msg.type === 'player' || msg.type === 'spell' ? 'text' :
    msg.type === 'ai_dm_choices' ? 'ai_dm' :
    msg.type;

  return {
    campaign_id: campaignId,
    user_id: userId || null,
    message_type: dbType,
    content: {
      sender: msg.sender,
      message: msg.message,
      diceResult: msg.diceResult,
      characterName: msg.characterName,
      rollType: msg.rollType,
      rollLabel: msg.rollLabel,
      spellData: msg.spellData,
      spellDamage: msg.spellDamage,
      spellHealing: msg.spellHealing,
      weaponDamage: msg.weaponDamage,
      savePrompt: msg.savePrompt,
      mobIntro: msg.mobIntro,
      npcSuggestion: msg.npcSuggestion,
      npcId: msg.npcId,
      npcName: msg.npcName,
      npcTitle: msg.npcTitle,
      factionColor: msg.factionColor,
      rollRequestData: msg.rollRequestData,
      choiceButtons: msg.choiceButtons,
    },
  };
}

// Convert DB format to ChatMessage
function dbToMessage(db: DbChatMessage): ChatMessage {
  const content = db.content as Record<string, unknown>;

  // Map DB message_type back to ChatMessage type
  let type: ChatMessage['type'];
  if (content.spellData) type = 'spell';
  else if (db.message_type === 'text') type = 'player';
  else if (db.message_type === 'ai_dm') type = 'ai_dm';
  else if (db.message_type === 'roll_request') type = 'roll_request';
  else type = db.message_type as ChatMessage['type'];

  return {
    id: db.id,
    sender: (content.sender as string) || 'Unknown',
    message: (content.message as string) || '',
    timestamp: new Date(db.created_at),
    type,
    diceResult: content.diceResult as DiceResult | undefined,
    characterName: content.characterName as string | undefined,
    rollType: content.rollType as ChatMessage['rollType'],
    rollLabel: content.rollLabel as RollLabel | undefined,
    spellData: content.spellData as Open5eSpell | undefined,
    spellDamage: content.spellDamage as ChatMessage['spellDamage'],
    spellHealing: content.spellHealing as ChatMessage['spellHealing'],
    weaponDamage: content.weaponDamage as ChatMessage['weaponDamage'],
    savePrompt: content.savePrompt as SavePrompt | undefined,
    mobIntro: content.mobIntro as ChatMessage['mobIntro'],
    npcSuggestion: content.npcSuggestion as ChatMessage['npcSuggestion'],
    npcId: content.npcId as string | undefined,
    npcName: content.npcName as string | undefined,
    npcTitle: content.npcTitle as string | undefined,
    factionColor: content.factionColor as string | undefined,
    rollRequestData: content.rollRequestData as RollRequestData | undefined,
    choiceButtons: content.choiceButtons as Array<{ label: string; action: string }> | undefined,
  };
}

export const useChatStore = create<ChatState>((set, get) => ({
  messages: [
    {
      id: '1',
      sender: 'System',
      message: 'Welcome to Machina! Chat and dice rolls will appear here.',
      timestamp: new Date(),
      type: 'system',
    },
  ],
  realtimeCleanup: null,
  currentCampaignId: null,
  currentUserId: null,
  isLoading: false,

  setCurrentUser: (userId) => set({ currentUserId: userId }),

  addMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };

    // Add to local state
    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    // Auto-sync to Supabase if we have a campaign
    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    }
  },

  beginStreamingMessage: (message) => {
    const newMessage: ChatMessage = {
      ...message,
      id: crypto.randomUUID(),
      timestamp: new Date(),
    };
    // Local-only — persisted once by finalizeStreamingMessage, otherwise every
    // chunk would fire a Supabase write.
    set((state) => ({ messages: [...state.messages, newMessage] }));
    return newMessage.id;
  },

  appendStreamingText: (id, text) => {
    set((state) => ({
      messages: state.messages.map((m) =>
        m.id === id ? { ...m, message: m.message + text } : m,
      ),
    }));
  },

  finalizeStreamingMessage: (id, final) => {
    let finalized: ChatMessage | undefined;
    set((state) => ({
      messages: state.messages.map((m) => {
        if (m.id !== id) return m;
        finalized = {
          ...m,
          ...(final?.message !== undefined ? { message: final.message } : {}),
          ...(final?.choiceButtons ? { choiceButtons: final.choiceButtons } : {}),
        };
        return finalized;
      }),
    }));

    // Persist the completed message once
    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (finalized && currentCampaignId) {
      saveMessageToSupabase(finalized, currentCampaignId, currentUserId || undefined);
    }
  },

  addDiceRoll: ({ sender, characterName, notation, result, rollType = 'normal', rollLabel, weaponDamage }) => {
    // Build display message based on roll type
    let displayMessage = `Rolled ${notation}`;
    if (rollType === 'advantage') {
      displayMessage = `Rolled ${notation} with advantage`;
    } else if (rollType === 'disadvantage') {
      displayMessage = `Rolled ${notation} with disadvantage`;
    }

    // Stamp the enriched notation onto the result so DiceResultDisplay
    // shows context (e.g. "1d20+2 (STR Save)") instead of raw dice ("1d20+2")
    const enrichedResult = notation !== result.notation
      ? { ...result, notation }
      : result;

    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender,
      message: displayMessage,
      timestamp: new Date(),
      type: 'dice',
      diceResult: enrichedResult,
      characterName,
      rollType,
      rollLabel,
      weaponDamage,
    };

    // Add to local state
    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    // Auto-sync to Supabase if we have a campaign
    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    console.log('[Chat] Dice roll sync - campaignId:', currentCampaignId, 'sender:', sender);
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    } else {
      console.warn('[Chat] No campaign ID set - dice roll not synced to Supabase');
    }
  },

  addSpellLookup: ({ sender, characterName, spell, spellDamage, spellHealing }) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender,
      characterName,
      message: `Looked up ${spell.name}`,
      timestamp: new Date(),
      type: 'spell',
      spellData: spell,
      spellDamage,
      spellHealing,
    };

    // Add to local state
    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    // Auto-sync to Supabase if we have a campaign
    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    }
  },

  addSavePrompt: ({ casterName, spellName, dc, ability }) => {
    const abilityShort = ability.slice(0, 3).toUpperCase();
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender: 'System',
      message: `${spellName} — ${abilityShort} Save DC ${dc}`,
      timestamp: new Date(),
      type: 'system',
      savePrompt: { dc, ability, spellName, casterName },
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    }
  },

  addMobIntro: (intro) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender: 'GM',
      message: intro.tagline || intro.description || `A ${intro.name} appears…`,
      timestamp: new Date(),
      type: 'system',
      mobIntro: intro,
    };

    set((state) => ({ messages: [...state.messages, newMessage] }));

    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    }
  },

  addNpcMessage: ({ npcId, npcName, npcTitle, factionColor, message }) => {
    const newMessage: ChatMessage = {
      id: crypto.randomUUID(),
      sender: npcName,
      message,
      timestamp: new Date(),
      type: 'npc',
      npcId,
      npcName,
      npcTitle,
      factionColor,
    };

    set((state) => ({
      messages: [...state.messages, newMessage],
    }));

    const { currentCampaignId, currentUserId, saveMessageToSupabase } = get();
    if (currentCampaignId) {
      saveMessageToSupabase(newMessage, currentCampaignId, currentUserId || undefined);
    }
  },

  addSystemMessage: (message) =>
    set((state) => ({
      messages: [
        ...state.messages,
        {
          id: crypto.randomUUID(),
          sender: 'System',
          message,
          timestamp: new Date(),
          type: 'system',
        },
      ],
    })),

  clearMessages: () =>
    set({
      messages: [
        {
          id: crypto.randomUUID(),
          sender: 'System',
          message: 'Chat cleared.',
          timestamp: new Date(),
          type: 'system',
        },
      ],
    }),

  // =======================================================================
  // SUPABASE SYNC
  // =======================================================================

  loadFromSupabase: async (campaignId, limit = 100) => {
    console.log('[Chat] Loading messages from Supabase for campaign:', campaignId);
    set({
      isLoading: true,
      currentCampaignId: campaignId,
      messages: [{
        id: crypto.randomUUID(),
        sender: 'System',
        message: 'Loading chat history...',
        timestamp: new Date(),
        type: 'system',
      }],
    });
    try {
      const { data, error } = await supabase
        .from('machina_chat_messages')
        .select('*')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: true })
        .limit(limit);

      if (error) throw error;

      if (get().currentCampaignId !== campaignId) {
        console.log('[Chat] Campaign changed during load, discarding results');
        set({ isLoading: false });
        return;
      }

      console.log('[Chat] Loaded', data?.length || 0, 'messages from Supabase');

      if (data && data.length > 0) {
        const messages = data.map((row) => dbToMessage(row as DbChatMessage));
        set({
          messages: [
            {
              id: '1',
              sender: 'System',
              message: 'Chat history loaded.',
              timestamp: new Date(),
              type: 'system',
            },
            ...messages,
          ],
          currentCampaignId: campaignId,
          isLoading: false,
        });
      } else {
        set({
          messages: [{
            id: crypto.randomUUID(),
            sender: 'System',
            message: 'Welcome to the campaign!',
            timestamp: new Date(),
            type: 'system',
          }],
          currentCampaignId: campaignId,
          isLoading: false,
        });
      }
    } catch (err) {
      console.error('[Chat] Failed to load chat history:', err);
      set({ isLoading: false });
    }
  },

  clearLocalState: () => {
    set({
      messages: [],
      currentCampaignId: null,
      isLoading: false,
    });
  },

  saveMessageToSupabase: async (message, campaignId, userId) => {
    // Single retry with backoff before surfacing failure. Keeps transient
    // network blips invisible while still catching real persistence failures.
    const dbMsg = messageToDb(message, campaignId, userId);
    const upsert = () => supabase
      .from('machina_chat_messages')
      .upsert({ ...dbMsg, id: message.id }, { onConflict: 'id', ignoreDuplicates: true });

    try {
      const { error } = await upsert();
      if (error) throw error;
      return;
    } catch (firstErr) {
      console.warn('[Chat] First save attempt failed, retrying in 1.5s:', firstErr);
    }

    // Backoff then retry once.
    await new Promise((resolve) => setTimeout(resolve, 1500));
    try {
      const { error } = await upsert();
      if (error) throw error;
      return;
    } catch (retryErr) {
      console.error('[Chat] Save retry failed — surfacing to user:', retryErr);
      const preview = message.message.length > 60
        ? message.message.slice(0, 57) + '…'
        : message.message;
      notify({
        level: 'error',
        message: 'Chat message failed to save',
        detail: `"${preview}" — refreshing will lose this message. Click retry.`,
        ttl: 0, // sticky — user must dismiss
        action: {
          label: 'Retry',
          onClick: () => {
            void get().saveMessageToSupabase(message, campaignId, userId);
          },
        },
      });
    }
  },

  subscribeToRealtime: (campaignId) => {
    get().unsubscribeFromRealtime();

    const { cleanup } = createSubscription({
      channelName: `chat:${campaignId}`,
      table: 'machina_chat_messages',
      filter: `campaign_id=eq.${campaignId}`,
      event: 'INSERT',
      onPayload: (payload) => {
        const newMessage = dbToMessage(payload.new as unknown as DbChatMessage);
        const { messages } = get();
        if (!messages.find((m) => m.id === newMessage.id)) {
          set({ messages: [...messages, newMessage] });
        }
      },
    });

    set({ realtimeCleanup: cleanup, currentCampaignId: campaignId });
  },

  unsubscribeFromRealtime: () => {
    const { realtimeCleanup } = get();
    if (realtimeCleanup) {
      realtimeCleanup();
      set({ realtimeCleanup: null });
    }
  },
}));
